import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { OrderStatus, type OrderSummary } from '@tucupet/shared';
import { PageHeader, Table, Select, Field, Pager } from '../components/ui';
import { api } from '../lib/api';

const STATUS_OPTIONS: { value: '' | OrderStatus; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: OrderStatus.PENDING_PAYMENT, label: 'Pago pendiente' },
  { value: OrderStatus.PENDING_AUTH, label: 'Autorizo pendiente' },
  { value: OrderStatus.CONFIRMED, label: 'Confirmada' },
  { value: OrderStatus.SURTIDO, label: 'Surtida' },
  { value: OrderStatus.CANCELLED, label: 'Cancelada' },
  { value: OrderStatus.EXPIRED, label: 'Expirada' },
];

export default function OrdersPage() {
  const [sp, setSp] = useSearchParams();
  const status = (sp.get('status') ?? '') as '' | OrderStatus;
  const search = sp.get('search') ?? '';
  const page = parseInt(sp.get('page') ?? '1', 10);

  const { data, isLoading } = useQuery<{ items: OrderSummary[]; total: number; page: number; limit: number }>({
    queryKey: ['admin', 'orders', status, search, page],
    queryFn: () => api('/api/admin/orders', { query: { status: status || undefined, search: search || undefined, page } }),
  });

  return (
    <div>
      <PageHeader kicker="ÓRDENES" title="Todas las órdenes" sub="Filtros, búsqueda por código y acciones por orden." />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 220 }}>
          <Select<'' | OrderStatus>
            label="Estado"
            value={status}
            onChange={(v) => setSp({ status: v, search, page: '1' })}
            options={STATUS_OPTIONS}
          />
        </div>
        <div style={{ minWidth: 260 }}>
          <Field label="Buscar código" value={search} onChange={(v) => setSp({ status, search: v, page: '1' })} placeholder="TC-…" />
        </div>
      </div>

      <Table<OrderSummary>
        rows={data?.items ?? []}
        empty={isLoading ? 'Cargando…' : 'Sin órdenes'}
        columns={[
          { key: 'code', label: 'Código', render: (r) => <Link to={`/orders/${r.id}`} className="mono" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>{r.code}</Link> },
          { key: 'station', label: 'Estación', render: (r) => r.stationName },
          { key: 'fuel', label: 'Combustible', render: (r) => `${r.fuelVariantName} · ${r.fuelOctane}` },
          { key: 'liters', label: 'Litros', render: (r) => <span className="mono">{r.liters} L</span> },
          { key: 'total', label: 'Total', render: (r) => <span className="mono">${r.total.toFixed(2)}</span> },
          { key: 'method', label: 'Método', render: (r) => <span className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{r.method ? r.method.toUpperCase() : '—'}</span> },
          {
            key: 'status',
            label: 'Estado',
            render: (r) => (
              <span
                className="mono"
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: r.status === 'cancelled' || r.status === 'expired' ? 'color-mix(in oklab, var(--danger) 14%, transparent)' : r.status === 'surtido' ? 'color-mix(in oklab, var(--accent) 14%, transparent)' : 'var(--surface-2)',
                  color: r.status === 'cancelled' || r.status === 'expired' ? 'var(--danger)' : r.status === 'surtido' ? 'var(--accent)' : 'var(--text-2)',
                  letterSpacing: '0.08em',
                }}
              >
                {r.status.toUpperCase()}
              </span>
            ),
          },
          { key: 'date', label: 'Fecha', render: (r) => <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{new Date(r.createdAt).toLocaleString('es-MX')}</span> },
        ]}
      />

      {data && <Pager total={data.total} page={data.page} limit={data.limit} onPage={(p) => setSp({ status, search, page: String(p) })} />}
    </div>
  );
}
